import React, { Component } from "react";
import Alert from "/src/util/alert";
import ResourceForm from "./ResourceForm";
import ResourceView from "./ResourceView";
import { emptyValueFor } from "./fields";
import { fetchItem, saveItem, deleteItem, patchItem, parseErrors, getDeactivation, isInactive } from "./useResource";
import { POINTS_ADMIN } from "../../config/lang";
import { translate } from "../../util/util";
import { getStore } from "../../util/redux/store";

const seed = descriptor => {
  const item = {};
  descriptor.fields.forEach(f => { item[f.name] = emptyValueFor(f); });
  return item;
};

export default class ResourcePage extends Component {
  constructor(props) {
    super(props);
    this.state = { mode: "VIEW", isNew: false, item: {}, errors: {}, changed: false, saving: false };
  }

  get me() { return this.props.currentUser || getStore().getState().me; }
  get lang() { return this.me.language; }
  get listPath() { return "/" + this.props.descriptor.slug; }

  componentDidMount() {
    this._isMounted = true;
    const { id } = this.props.match.params;
    const { descriptor } = this.props;
    if (id === "new") {
      this.setState({ mode: "EDIT", isNew: true, item: seed(descriptor) });
    } else {
      fetchItem(descriptor, id, item => this.safeSetState({ item: item || {} }));
    }
  }

  componentWillUnmount() { this._isMounted = false; }

  safeSetState = (...args) => { if (this._isMounted) this.setState(...args); };

  onChange = (name, value) => {
    this.setState(s => ({ item: { ...s.item, [name]: value }, changed: true,
      errors: { ...s.errors, [name]: undefined } }));
  };

  submit = () => {
    const { descriptor } = this.props;
    const { item } = this.state;
    const wasNew = !item.id;
    // required-field validation
    const errors = {};
    descriptor.fields.forEach(f => {
      if (f.required) {
        const v = item[f.name];
        if (v === null || v === undefined || v === "") errors[f.name] = translate(POINTS_ADMIN, "required", this.lang);
      }
    });
    if (Object.keys(errors).length) return this.setState({ errors });

    this.setState({ saving: true });
    saveItem(descriptor, item, this.me, r => {
      if (r && r.id) {
        Alert.success(translate(POINTS_ADMIN, "saved", this.lang), { position: "bottom-right" });
        this.safeSetState({ item: r, mode: "VIEW", isNew: false, changed: false, errors: {}, saving: false });
        if (wasNew) this.props.history.replace(`${this.listPath}/${r.id}`);
      } else if (r && typeof r.json === "function") {
        parseErrors(r).then(parsed => {
          this.safeSetState({ errors: parsed.fields });
          this.safeSetState({ saving: false });
          Alert.warning(parsed.message || translate(POINTS_ADMIN, "save_error", this.lang), { position: "bottom-right" });
        });
      } else {
        this.safeSetState({ saving: false });
        Alert.warning(translate(POINTS_ADMIN, "save_error", this.lang), { position: "bottom-right" });
      }
    });
  };

  remove = () => {
    const { descriptor } = this.props;
    const deact = getDeactivation(descriptor);
    const confirmKey = deact ? "confirm_deactivate" : "confirm_delete";
    if (!window.confirm(translate(POINTS_ADMIN, confirmKey, this.lang))) return;

    if (deact && deact.via === "patch") {
      patchItem(descriptor, this.state.item.id, { [deact.field]: deact.inactiveValue }, r => {
        if (r && r.id) {
          Alert.success(translate(POINTS_ADMIN, "deleted", this.lang), { position: "bottom-right" });
          this.safeSetState({ item: r });
        } else {
          Alert.warning(translate(POINTS_ADMIN, "save_error", this.lang), { position: "bottom-right" });
        }
      });
      return;
    }

    deleteItem(descriptor, this.state.item.id, ok => {
      if (ok) {
        Alert.success(translate(POINTS_ADMIN, "deleted", this.lang), { position: "bottom-right" });
        if (deact) {
          fetchItem(descriptor, this.state.item.id, it => this.safeSetState({ item: it || this.state.item }));
        } else {
          this.props.history.replace(this.listPath);
        }
      } else {
        Alert.warning(translate(POINTS_ADMIN, "save_error", this.lang), { position: "bottom-right" });
      }
    });
  };

  activate = () => {
    const { descriptor } = this.props;
    const deact = getDeactivation(descriptor);
    if (!deact) return;
    patchItem(descriptor, this.state.item.id, { [deact.field]: deact.activeValue }, r => {
      if (r && r.id) {
        Alert.success(translate(POINTS_ADMIN, "saved", this.lang), { position: "bottom-right" });
        this.safeSetState({ item: r });
      } else {
        Alert.warning(translate(POINTS_ADMIN, "save_error", this.lang), { position: "bottom-right" });
      }
    });
  };

  toggleMode = () => this.setState(s => ({ mode: s.mode === "EDIT" ? "VIEW" : "EDIT" }));

  render() {
    const { descriptor } = this.props;
    const { item, mode, isNew, errors, saving } = this.state;
    const G = POINTS_ADMIN, lang = this.lang;
    const deact = getDeactivation(descriptor);
    const readOnly = descriptor.readOnly === true;
    const editing = mode === "EDIT" && !readOnly;
    return (
      <div className="container-fluid mp-page">
        <div className="mp-top-bar">
          <button className="mp-back-btn" onClick={() => this.props.history.replace(this.listPath)}>
            <span className="material-symbols-outlined">arrow_back</span>
            {translate(G, "back_btn", lang)}
          </button>
          <div className="pt-top-actions">
            {!isNew && !readOnly && (
              <button className="mp-toggle-btn" onClick={this.toggleMode}>
                <span className="material-symbols-outlined">{mode === "EDIT" ? "visibility" : "edit"}</span>
                {mode === "EDIT" ? translate(G, "view_btn", lang) : translate(G, "edit_btn", lang)}
              </button>
            )}
            {!isNew && deact && (
              isInactive(item, deact)
                ? (
                  <button className="mp-toggle-btn pt-activate-btn" onClick={this.activate}>
                    <span className="material-symbols-outlined">restore</span>
                    {translate(G, "activate_btn", lang)}
                  </button>
                )
                : (
                  <button className="mp-toggle-btn pt-delete-btn" onClick={this.remove}>
                    <span className="material-symbols-outlined">delete</span>
                    {translate(G, "deactivate_btn", lang)}
                  </button>
                )
            )}
            {!isNew && !deact && (
              <button className="mp-toggle-btn pt-delete-btn" onClick={this.remove}>
                <span className="material-symbols-outlined">delete</span>
                {translate(G, "delete_btn", lang)}
              </button>
            )}
          </div>
        </div>
        <div className="mp-profile-card">
          {editing
            ? <>
                <ResourceForm descriptor={descriptor} item={item} errors={errors} onChange={this.onChange} lang={lang} />
                <div className="mp-edit-actions">
                  <button className={`mp-save-btn${saving ? " mp-save-btn--disabled" : ""}`} onClick={this.submit} disabled={this.state.saving}>
                    <span className="material-symbols-outlined">save</span>
                    {translate(G, "save_btn", lang)}
                  </button>
                </div>
              </>
            : <ResourceView descriptor={descriptor} item={item} lang={lang} />}
        </div>
      </div>
    );
  }
}
